<template>
  <v-card variant="outlined">
    <v-card-title class="d-flex align-center">
      <span>Filtros</span>

      <v-spacer />

      <v-btn color="primary" prepend-icon="mdi-plus" @click="addFilter">
        Adicionar filtro
      </v-btn>
    </v-card-title>

    <v-card-text>
      <v-alert v-if="filters.length === 0" type="info" variant="tonal" class="mb-4">
        No existen filtros configurados.
      </v-alert>

      <v-row
        v-for="(filter, index) in filters"
        :key="filter.id"
        align="center"
        class="mb-2"
      >
        <!-- Conector AND / OR -->
        <v-col v-if="index > 0" cols="12" md="2">
          <v-select
            v-model="filter.logicalOperator"
            label="Condición"
            :items="logicalOperators"
            item-title="label"
            item-value="value"
            density="compact"
            hide-details
          />
        </v-col>

        <!-- Campo -->
        <v-col cols="12" :md="index > 0 ? 3 : 4">
          <v-autocomplete
            v-model="filter.field"
            label="Campo"
            :items="availableFields"
            item-title="DisplayName"
            return-object
            density="compact"
            clearable
            hide-details
            @update:model-value="onFieldChanged(filter)"
          >
            <template #item="{ props, item }">
              <v-list-item
                v-bind="props"
                :title="item.raw.PropertyName"
                :subtitle="item.raw.ColumnName"
              />
            </template>
          </v-autocomplete>
        </v-col>

        <!-- Operador -->
        <v-col cols="12" md="3">
          <v-select
            v-model="filter.operator"
            label="Operador"
            :items="getOperators(filter.field)"
            item-title="label"
            item-value="value"
            density="compact"
            hide-details
            :disabled="!filter.field"
            @update:model-value="onOperatorChanged(filter)"
          />
        </v-col>

        <!-- Valor booleano -->
        <v-col v-if="isBooleanField(filter.field)" cols="12" md="3">
          <v-select
            v-model="filter.value"
            label="Valor"
            :items="booleanOptions"
            item-title="label"
            item-value="value"
            density="compact"
            hide-details
            :disabled="!filter.operator || doesNotRequireValue(filter.operator)"
          />
        </v-col>

        <!-- Valor normal -->
        <v-col v-else cols="12" md="3">
          <v-text-field
            v-model="filter.value"
            :label="getValueLabel(filter)"
            :type="getInputType(filter.field)"
            density="compact"
            hide-details
            :disabled="
              !filter.field || !filter.operator || doesNotRequireValue(filter.operator)
            "
          />
        </v-col>

        <!-- Eliminar -->
        <v-col cols="12" md="1" class="text-center">
          <v-btn
            icon="mdi-delete-outline"
            color="error"
            variant="text"
            @click="removeFilter(index)"
          />
        </v-col>
      </v-row>

      <v-divider v-if="filters.length > 0" class="my-5" />

      <v-textarea
        :model-value="expression"
        label="Expresión generada"
        variant="outlined"
        rows="3"
      />

      <div class="d-flex justify-end ga-2">
        <v-btn variant="outlined" @click="clearFilters"> Limpiar </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "FilterBuilder",

  props: {
    /*
     * Todos los campos disponibles de la entidad.
     */
    entityFields: {
      type: Array,
      default: () => [],
    },

    /*
     * Nombres de los campos que fueron seleccionados inicialmente.
     */
    selectedFieldNames: {
      type: Array,
      default: () => [],
    },

    /*
     * Nombres dato seleccionado
     */
    fieldNames: {
      type: String,
      default: "",
    },
  },

  emits: ["update:fieldNames"],

  data() {
    return {
      filters: [],
      registerstart: false,
      logicalOperators: [
        {
          label: "Y",
          value: "AND",
        },
        {
          label: "O",
          value: "OR",
        },
      ],

      booleanOptions: [
        {
          label: "Verdadero",
          value: true,
        },
        {
          label: "Falso",
          value: false,
        },
      ],

      commonOperators: [
        {
          label: "Igual a",
          value: "==",
        },
        {
          label: "Diferente de",
          value: "!=",
        },
      ],

      numberOperators: [
        {
          label: "Igual a",
          value: "==",
        },
        {
          label: "Diferente de",
          value: "!=",
        },
        {
          label: "Mayor que",
          value: ">",
        },
        {
          label: "Mayor o igual que",
          value: ">=",
        },
        {
          label: "Menor que",
          value: "<",
        },
        {
          label: "Menor o igual que",
          value: "<=",
        },
      ],

      stringOperators: [
        {
          label: "Igual a",
          value: "==",
        },
        {
          label: "Diferente de",
          value: "!=",
        },
        {
          label: "Contiene",
          value: "contains",
        },
        {
          label: "No contiene",
          value: "notContains",
        },
        {
          label: "Comienza por",
          value: "startsWith",
        },
        {
          label: "Termina en",
          value: "endsWith",
        },
      ],

      nullableOperators: [
        {
          label: "Es nulo",
          value: "isNull",
        },
        {
          label: "No es nulo",
          value: "isNotNull",
        },
      ],
    };
  },

  computed: {
    availableFields() {
      return this.entityFields.map((field) => ({
        ...field,

        DisplayName:
          field.PropertyName === field.ColumnName
            ? field.PropertyName
            : `${field.PropertyName} (${field.ColumnName})`,
      }));
    },

    expression() {
      const validFilters = this.filters.filter(this.isFilterComplete);

      return validFilters
        .map((filter, index) => {
          const condition = this.buildCondition(filter);

          if (index === 0) {
            return condition;
          }

          const connector = filter.logicalOperator === "OR" ? "||" : "&&";

          return `${connector} ${condition}`;
        })
        .join(" ");
    },

    isValid() {
      return this.filters.length > 0 && this.filters.every(this.isFilterComplete);
    },
  },

  watch: {
    expression: {
      immediate: true,

      handler(newExpression) {
        this.$emit("update:fieldNames", newExpression);
      },
    },

    selectedFieldNames: {
      immediate: true,
      deep: true,

      handler() {
        this.initializeSelectedFields();
      },
    },

    entityFields: {
      deep: true,

      handler() {
        this.initializeSelectedFields();
      },
    },
  },

  methods: {
    createId() {
      if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
      }

      return `${Date.now()}-${Math.random()}`;
    },

    initializeSelectedFields() {
      if (!this.entityFields.length) {
        return;
      }

      this.filters = this.selectedFieldNames
        .map((fieldName) => {
          return this.availableFields.find(
            (field) => field.PropertyName === fieldName || field.ColumnName === fieldName
          );
        })
        .filter((field) => field !== undefined)
        .map((field) => ({
          id: this.createId(),
          logicalOperator: "AND",
          field,
          operator: "",
          value: null,
        }));

      if (this.fieldNames) {
        this.clearFilters();
      }
    },

    addFilter() {
      this.filters.push({
        id: this.createId(),
        logicalOperator: "AND",
        field: null,
        operator: "",
        value: null,
      });
    },

    removeFilter(index) {
      this.filters.splice(index, 1);
    },

    clearFilters() {
      this.filters = [];
    },

    onFieldChanged(filter) {
      filter.operator = "";
      filter.value = null;
    },

    onOperatorChanged(filter) {
      if (this.doesNotRequireValue(filter.operator)) {
        filter.value = null;
      }
    },

    getOperators(field) {
      if (!field) {
        return [];
      }

      let operators = [];

      if (this.isStringField(field)) {
        operators = [...this.stringOperators];
      } else if (this.isNumberField(field) || this.isDateField(field)) {
        operators = [...this.numberOperators];
      } else {
        operators = [...this.commonOperators];
      }

      if (field.isNullable) {
        operators.push(...this.nullableOperators);
      }

      return operators;
    },

    isFilterComplete(filter) {
      if (!filter.field || !filter.operator) {
        return false;
      }

      if (this.doesNotRequireValue(filter.operator)) {
        return true;
      }

      return filter.value !== null && filter.value !== undefined && filter.value !== "";
    },

    buildCondition(filter) {
      if (!filter.field) {
        return "";
      }

      /*
       * Se usa propertyName porque la expresión se ejecutará
       * sobre la entidad de Entity Framework.
       */
      const fieldName = filter.field.PropertyName;
      const value = this.formatValue(filter);

      switch (filter.operator) {
        case "contains":
          return `${fieldName}.Contains(${value})`;

        case "notContains":
          return `!${fieldName}.Contains(${value})`;

        case "startsWith":
          return `${fieldName}.StartsWith(${value})`;

        case "endsWith":
          return `${fieldName}.EndsWith(${value})`;

        case "isNull":
          return `${fieldName} == null`;

        case "isNotNull":
          return `${fieldName} != null`;

        default:
          return `${fieldName} ${filter.operator} ${value}`;
      }
    },

    formatValue(filter) {
      const field = filter.field;

      if (!field) {
        return "";
      }

      if (this.isStringField(field)) {
        return `"${this.escapeString(String(filter.value))}"`;
      }

      if (this.isDateField(field)) {
        return `"${String(filter.value)}"`;
      }

      if (this.isBooleanField(field)) {
        return filter.value ? "true" : "false";
      }

      return String(filter.value);
    },

    escapeString(value) {
      return value.replaceAll("\\", "\\\\").replaceAll('"', '\\"');
    },

    getInputType(field) {
      if (!field) {
        return "text";
      }

      if (this.isNumberField(field)) {
        return "number";
      }

      if (this.isDateField(field)) {
        return "date";
      }

      return "text";
    },

    getValueLabel(filter) {
      switch (filter.operator) {
        case "contains":
        case "notContains":
          return "Texto a buscar";

        case "startsWith":
          return "Texto inicial";

        case "endsWith":
          return "Texto final";

        default:
          return "Valor";
      }
    },

    doesNotRequireValue(operator) {
      return operator === "isNull" || operator === "isNotNull";
    },

    normalizeType(type) {
      if (!type) {
        return "";
      }

      return type.replace("System.", "").replace("?", "");
    },

    isStringField(field) {
      if (!field) {
        return false;
      }

      const type = this.normalizeType(field.CSharpType);

      return ["String", "string", "Char", "char"].includes(type);
    },

    isNumberField(field) {
      if (!field) {
        return false;
      }

      const type = this.normalizeType(field.CSharpType);

      const numericTypes = [
        "Byte",
        "SByte",
        "Int16",
        "Int32",
        "Int64",
        "UInt16",
        "UInt32",
        "UInt64",
        "Single",
        "Double",
        "Decimal",
        "byte",
        "short",
        "int",
        "long",
        "float",
        "double",
        "decimal",
      ];

      return numericTypes.includes(type);
    },

    isDateField(field) {
      if (!field) {
        return false;
      }

      const type = this.normalizeType(field.CSharpType);

      return ["DateTime", "DateTimeOffset", "DateOnly"].includes(type);
    },

    isBooleanField(field) {
      if (!field) {
        return false;
      }

      const type = this.normalizeType(field.CSharpType);

      return ["Boolean", "bool"].includes(type);
    },

    convertValueByType(filter) {
      if (this.doesNotRequireValue(filter.operator)) {
        return null;
      }

      if (this.isNumberField(filter.field)) {
        return Number(filter.value);
      }

      if (this.isBooleanField(filter.field)) {
        return Boolean(filter.value);
      }

      return filter.value;
    },
  },
};
</script>
